import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DollarSignIcon, BuildingIcon, CalendarIcon, FileTextIcon, CheckCircleIcon } from 'lucide-react';
export function BillFinancingWizard() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    loanPackage: '',
    financingAmount: '',
    requestedTerm: '30',
    purpose: '',
    additionalNotes: ''
  });
  // Mock bill data
  const bill = {
    id: id,
    billNumber: 'BILL-2024-002',
    supplierName: 'Spice Traders Co',
    amount: 87500,
    dueDate: '2024-02-20'
  };
  const dfccLoanPackages = [{
    id: 'standard',
    name: 'Standard Financing',
    interestRate: '12%',
    maxTerm: '90 days',
    processingTime: '48 hours',
    description: 'Ideal for regular business operations with flexible terms'
  }, {
    id: 'premium',
    name: 'Premium Financing',
    interestRate: '10.5%',
    maxTerm: '120 days',
    processingTime: '24 hours',
    description: 'Lower rates for established businesses with priority processing'
  }, {
    id: 'express',
    name: 'Express Financing',
    interestRate: '14%',
    maxTerm: '60 days',
    processingTime: '12 hours',
    description: 'Quick approval for urgent cash flow needs'
  }, {
    id: 'sme',
    name: 'SME Growth Package',
    interestRate: '11%',
    maxTerm: '90 days',
    processingTime: '36 hours',
    description: 'Special rates for small and medium enterprises'
  }];
  const handleSubmit = () => {
    console.log('Submitting financing request:', {
      billId: id,
      ...formData
    });
    navigate('/bills');
  };
  const selectedPackage = dfccLoanPackages.find(p => p.id === formData.loanPackage);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-3769">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3770">
        <button onClick={() => navigate(`/bill-detail/${id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-3771">
          <ArrowLeftIcon size={20} data-id="element-3772" />
          {t('pages.BillFinancingWizard.back_to_bill')}
        </button>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3773">
        <div className="flex items-center justify-between mb-8" data-id="element-3774">
          <div className="flex-1" data-id="element-3775">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3776">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3777">
                1
              </div>
              <div className="ml-3" data-id="element-3778">
                <div className="text-sm font-medium" data-id="element-3779">
                  {t('pages.BillApprovalWizard.bill_details')}
                </div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3780"></div>
          <div className="flex-1" data-id="element-3781">
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3782">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3783">
                2
              </div>
              <div className="ml-3" data-id="element-3784">
                <div className="text-sm font-medium" data-id="element-3785">
                  {t('pages.BillFinancingWizard.select_provider')}
                </div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3786"></div>
          <div className="flex-1" data-id="element-3787">
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3788">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3789">
                3
              </div>
              <div className="ml-3" data-id="element-3790">
                <div className="text-sm font-medium" data-id="element-3791">
                  {t('pages.BillFinancingWizard.financing_terms')}
                </div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3792"></div>
          <div className="flex-1" data-id="element-3793">
            <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3794">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3795">
                4
              </div>
              <div className="ml-3" data-id="element-3796">
                <div className="text-sm font-medium" data-id="element-3797">
                  {t('pages.BillFinancingWizard.review_submit')}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Step 1: Bill Details */}
        {step === 1 && <div className="space-y-6" data-id="element-3798">
            <div data-id="element-3799">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3800">
                {t('pages.BillFinancingWizard.bill_information')}
              </h2>
              <p className="text-gray-600" data-id="element-3801">
                {t('pages.BillFinancingWizard.review_the_bill_details_for_financing')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-3802">
              <div className="grid grid-cols-2 gap-4" data-id="element-3803">
                <div data-id="element-3804">
                  <div className="text-sm text-gray-500" data-id="element-3805">
                    {t('pages.BillFinancingWizard.bill_number')}
                  </div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3806">
                    {bill.billNumber}
                  </div>
                </div>
                <div data-id="element-3807">
                  <div className="text-sm text-gray-500" data-id="element-3808">
                    {t('pages.BillFinancingWizard.supplier')}
                  </div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3809">
                    {bill.supplierName}
                  </div>
                </div>
                <div data-id="element-3810">
                  <div className="text-sm text-gray-500" data-id="element-3811">
                    {t('pages.BillDetail.due_date')}
                  </div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3812">
                    {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div data-id="element-3813">
                  <div className="text-sm text-gray-500" data-id="element-3814">
                    {t('pages.BillFinancingWizard.bill_amount')}
                  </div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-3815">
                    {bill.currency} {bill.amount.toLocaleString('en-US')}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-3816">
              <div className="flex items-start gap-3" data-id="element-3817">
                <DollarSignIcon size={20} className="text-blue-600 mt-0.5" data-id="element-3818" />
                <div className="text-sm text-blue-800" data-id="element-3819">
                  <div className="font-medium mb-1" data-id="element-3820">
                    {t('pages.BillFinancingWizard.bill_financing_benefits')}
                  </div>
                  <ul className="list-disc list-inside space-y-1" data-id="element-3821">
                    <li data-id="element-3822">
                      {t('pages.BillFinancingWizard.improve_cash_flow_by_paying_suppliers')}
                    </li>
                    <li data-id="element-3823">
                      {t('pages.BillFinancingWizard.maintain_good_supplier_relationships')}
                    </li>
                    <li data-id="element-3824">
                      {t('pages.BillFinancingWizard.take_advantage_of_early_payment_discounts')}
                    </li>
                    <li data-id="element-3825">
                      {t('pages.BillFinancingWizard.flexible_repayment_terms')}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-3826">
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-3827">
                {t('pages.BillFinancingWizard.continue')}
              </button>
            </div>
          </div>}
        {/* Step 2: Select Provider */}
        {step === 2 && <div className="space-y-6" data-id="element-3828">
            <div data-id="element-3829">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3830">
                {t('pages.BillFinancingWizard.select_pomelo_financing_package')}
              </h2>
              <p className="text-gray-600" data-id="element-3831">
                {t('pages.BillFinancingWizard.choose_a_financing_package_that_best')}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-3832">
              <div className="flex items-start gap-3" data-id="element-3833">
                <BuildingIcon size={20} className="text-blue-600 mt-0.5" data-id="element-3834" />
                <div data-id="element-3835">
                  <div className="font-semibold text-blue-900 mb-1" data-id="element-3836">
                    {t('pages.BillFinancingWizard.pomelo_your_trusted_financial_partner')}
                  </div>
                  <div className="text-sm text-blue-800" data-id="element-3837">
                    {t('pages.BillFinancingWizard.as_a_valued_pomelo_customer_you')}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-3838">
              {dfccLoanPackages.map(pkg => <label key={pkg.id} className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${formData.loanPackage === pkg.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-3839">
                  <input type="radio" name="loanPackage" value={pkg.id} checked={formData.loanPackage === pkg.id} onChange={e => setFormData({
              ...formData,
              loanPackage: e.target.value
            })} className="sr-only" data-id="element-3840" />

                  <div className="flex items-start justify-between" data-id="element-3841">
                    <div className="flex-1" data-id="element-3842">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3843">
                        <div className="text-lg font-semibold text-gray-900" data-id="element-3844">
                          {pkg.name}
                        </div>
                        {formData.loanPackage === pkg.id && <CheckCircleIcon size={20} className="text-blue-600" data-id="element-3845" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-3846">
                        {pkg.description}
                      </p>
                      <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-3847">
                        <div data-id="element-3848">
                          <div className="text-gray-500" data-id="element-3849">
                            {t('pages.BillFinancingWizard.interest_rate')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-3850">
                            {pkg.interestRate}
                          </div>
                        </div>
                        <div data-id="element-3851">
                          <div className="text-gray-500" data-id="element-3852">
                            {t('pages.BillFinancingWizard.max_term')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-3853">
                            {pkg.maxTerm}
                          </div>
                        </div>
                        <div data-id="element-3854">
                          <div className="text-gray-500" data-id="element-3855">
                            {t('pages.BillFinancingWizard.processing_time')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-3856">
                            {pkg.processingTime}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>)}
            </div>
            <div className="flex justify-between" data-id="element-3857">
              <button onClick={() => setStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3858">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={() => setStep(3)} disabled={!formData.loanPackage} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3859">
                {t('pages.BillFinancingWizard.continue')}
              </button>
            </div>
          </div>}
        {/* Step 3: Financing Terms */}
        {step === 3 && <div className="space-y-6" data-id="element-3860">
            <div data-id="element-3861">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3862">
                {t('pages.BillFinancingWizard.financing_terms')}
              </h2>
              <p className="text-gray-600" data-id="element-3863">
                {t('pages.BillFinancingWizard.specify_the_financing_amount_and_terms')}
              </p>
            </div>
            <div className="space-y-6" data-id="element-3864">
              <div data-id="element-3865">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3866">
                  {t('pages.BillFinancingWizard.financing_amount_usd')}
                </label>
                <input type="number" value={formData.financingAmount} onChange={e => setFormData({
              ...formData,
              financingAmount: e.target.value
            })} placeholder={t('pages.BillFinancingWizard.attrs.enter_amount')} max={bill.amount} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3867" />

                <div className="mt-1 text-sm text-gray-500" data-id="element-3868">
                  {t('pages.BillFinancingWizard.maximum')}{' '}
                  {bill.amount.toLocaleString('en-US')}
                </div>
              </div>
              <div data-id="element-3869">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3870">
                  {t('pages.BillFinancingWizard.requested_term_days')}
                </label>
                <select value={formData.requestedTerm} onChange={e => setFormData({
              ...formData,
              requestedTerm: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3871">
                  <option value="15" data-id="element-3872">
                    {t('pages.BillFinancingWizard.15_days')}
                  </option>
                  <option value="30" data-id="element-3873">
                    {t('pages.BillFinancingWizard.30_days')}
                  </option>
                  <option value="45" data-id="element-3874">
                    {t('pages.BillFinancingWizard.45_days')}
                  </option>
                  <option value="60" data-id="element-3875">
                    {t('pages.BillFinancingWizard.60_days')}
                  </option>
                  <option value="90" data-id="element-3876">
                    {t('pages.BillFinancingWizard.90_days')}
                  </option>
                </select>
              </div>
              <div data-id="element-3877">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3878">
                  {t('pages.BillFinancingWizard.purpose_of_financing')}
                </label>
                <select value={formData.purpose} onChange={e => setFormData({
              ...formData,
              purpose: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3879">
                  <option value="" data-id="element-3880">
                    {t('pages.BillFinancingWizard.select_purpose')}
                  </option>
                  <option value="early_payment" data-id="element-3881">
                    {t('pages.BillFinancingWizard.early_payment_discount')}
                  </option>
                  <option value="cash_flow" data-id="element-3882">
                    {t('pages.BillFinancingWizard.cash_flow_management')}
                  </option>
                  <option value="supplier_relationship" data-id="element-3883">
                    {t('pages.BillFinancingWizard.supplier_relationship')}
                  </option>
                  <option value="other" data-id="element-3884">
                    {t('pages.BillFinancingWizard.other')}
                  </option>
                </select>
              </div>
              <div data-id="element-3885">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3886">
                  {t('pages.BillFinancingWizard.additional_notes')}
                </label>
                <textarea value={formData.additionalNotes} onChange={e => setFormData({
              ...formData,
              additionalNotes: e.target.value
            })} rows={4} placeholder={t('pages.BillFinancingWizard.attrs.any_additional_information')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3887" />
              </div>
            </div>
            <div className="flex justify-between" data-id="element-3888">
              <button onClick={() => setStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3889">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={() => setStep(4)} disabled={!formData.financingAmount || !formData.requestedTerm || !formData.purpose} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3890">
                {t('pages.BillFinancingWizard.review_request')}
              </button>
            </div>
          </div>}
        {/* Step 4: Review & Submit */}
        {step === 4 && <div className="space-y-6" data-id="element-3891">
            <div data-id="element-3892">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3893">
                {t('pages.BillFinancingWizard.review_financing_request')}
              </h2>
              <p className="text-gray-600" data-id="element-3894">
                {t('pages.BillFinancingWizard.please_review_your_financing_request_before')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-6" data-id="element-3895">
              <div data-id="element-3896">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-3897">
                  {t('pages.BillFinancingWizard.bill_information_2')}
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-3898">
                  <div data-id="element-3899">
                    <div className="text-sm text-gray-500" data-id="element-3900">
                      {t('pages.BillFinancingWizard.bill_number')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3901">
                      {bill.billNumber}
                    </div>
                  </div>
                  <div data-id="element-3902">
                    <div className="text-sm text-gray-500" data-id="element-3903">
                      {t('pages.BillFinancingWizard.supplier')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3904">
                      {bill.supplierName}
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-3905">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-3906">
                  {t('pages.BillFinancingWizard.financing_details')}
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-3907">
                  <div data-id="element-3908">
                    <div className="text-sm text-gray-500" data-id="element-3909">
                      {t('pages.BillFinancingWizard.bank')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3910">
                      {t('pages.BillFinancingWizard.pomelo')}
                    </div>
                  </div>
                  <div data-id="element-3911">
                    <div className="text-sm text-gray-500" data-id="element-3912">
                      {t('pages.BillFinancingWizard.package')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3913">
                      {selectedPackage?.name}
                    </div>
                  </div>
                  <div data-id="element-3914">
                    <div className="text-sm text-gray-500" data-id="element-3915">
                      {t('pages.BillFinancingWizard.interest_rate')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3916">
                      {selectedPackage?.interestRate}
                    </div>
                  </div>
                  <div data-id="element-3917">
                    <div className="text-sm text-gray-500" data-id="element-3918">
                      {t('pages.BillFinancingWizard.processing_time')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3919">
                      {selectedPackage?.processingTime}
                    </div>
                  </div>
                  <div data-id="element-3920">
                    <div className="text-sm text-gray-500" data-id="element-3921">
                      {t('pages.BillFinancingWizard.financing_amount')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3922">
                      {t('pages.BillFinancingWizard.rs')}{' '}
                      {parseFloat(formData.financingAmount).toLocaleString('en-US')}
                    </div>
                  </div>
                  <div data-id="element-3923">
                    <div className="text-sm text-gray-500" data-id="element-3924">
                      {t('pages.BillFinancingWizard.term')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3925">
                      {formData.requestedTerm}{' '}
                      {t('pages.BillFinancingWizard.days')}
                    </div>
                  </div>
                  <div data-id="element-3926">
                    <div className="text-sm text-gray-500" data-id="element-3927">
                      {t('pages.BillFinancingWizard.purpose')}
                    </div>
                    <div className="text-base font-medium text-gray-900 capitalize" data-id="element-3928">
                      {formData.purpose.replace('_', ' ')}
                    </div>
                  </div>
                </div>
              </div>
              {formData.additionalNotes && <div className="pt-4 border-t border-gray-200" data-id="element-3929">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-3930">
                    {t('pages.BillFinancingWizard.additional_notes_2')}
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-3931">
                    {formData.additionalNotes}
                  </p>
                </div>}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-3932">
              <div className="flex items-start gap-3" data-id="element-3933">
                <FileTextIcon size={20} className="text-blue-600 mt-0.5" data-id="element-3934" />
                <div className="text-sm text-blue-800" data-id="element-3935">
                  {t('pages.BillFinancingWizard.by_submitting_this_request_you_agree')}{' '}
                  {selectedPackage?.name}
                  {t('pages.BillFinancingWizard.the_request_will_be_processed_within')}{' '}
                  {selectedPackage?.processingTime}.
                </div>
              </div>
            </div>
            <div className="flex justify-between" data-id="element-3936">
              <button onClick={() => setStep(3)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3937">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={handleSubmit} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-3938">
                {t('pages.BillFinancingWizard.submit_financing_request')}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}